declare const process: {
  env: Record<string, string | undefined>;
};

const readEnv = (value: string | undefined, key: string, defaultValue?: string): string => {
  if (!value && defaultValue === undefined) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value || defaultValue || '';
};

const readEnvNumber = (value: string | undefined, key: string, defaultValue: number): number => {
  if (!value) return defaultValue;
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    console.warn(`Invalid number for ${key}, using default: ${defaultValue}`);
    return defaultValue;
  }
  return parsed;
};

// API Base URLs
export const API_BASE_URL = readEnv(process.env.API_BASE_URL, 'API_BASE_URL', '');
export const DATA_DELIVERY_URL = readEnv(process.env.DATA_DELIVERY_URL, 'DATA_DELIVERY_URL', '');
export const ISTARI_BASE_URL = readEnv(process.env.ISTARI_BASE_URL, 'ISTARI_BASE_URL', '');
export const ISTARI_NEW_BASE_URL = readEnv(process.env.ISTARI_NEW_BASE_URL, 'ISTARI_NEW_BASE_URL', '');
export const DATA_FEED_BASE_URL = readEnv(process.env.DATA_FEED_BASE_URL, 'DATA_FEED_BASE_URL', '');
export const RMS_BASE_URL = readEnv(process.env.RMS_BASE_URL, 'RMS_BASE_URL', '');

// Development proxy URLs (webpack dev server)
export const ISTARI_NEW_PROXY_URL = '/';
export const ISTARI_PROXY_URL = '/';
export const DATA_DELIVERY_PROXY_URL = '/';
export const LEGACY_PROXY_URL = '/legacy-api';

// API Authentication Credentials (VAPT V-001): the shared service credentials are
// NO LONGER read into the bundle. They live only in the edge (nginx/njs) at runtime
// and are attached server-side. Do not re-add DATA_DELIVERY_V2_* / CONTACT_EMAIL_BASIC_*
// here — that would recompile them (or their key names) back into the public JS.

// Temp Token
export const TEMP_TOKEN = readEnv(process.env.TEMP_TOKEN, 'TEMP_TOKEN','');

// Timeouts (in minutes)
export const HTTP_TIMEOUT_MINUTES = readEnvNumber(process.env.HTTP_TIMEOUT_MINUTES, 'HTTP_TIMEOUT_MINUTES', 10);
export const HTTP_TIMEOUT_SHORT_MINUTES = readEnvNumber(process.env.HTTP_TIMEOUT_SHORT_MINUTES, 'HTTP_TIMEOUT_SHORT_MINUTES', 2);
export const HTTP_TIMEOUT_LONG_MINUTES = readEnvNumber(process.env.HTTP_TIMEOUT_LONG_MINUTES, 'HTTP_TIMEOUT_LONG_MINUTES', 30);

// User Agent
export const USER_AGENT = readEnv(process.env.USER_AGENT, 'USER_AGENT', 'InsyncAddinRequester');

// Contact support email settings (matches legacy COM add-in defaults)
export const CONTACT_EMAIL_FROM = readEnv(process.env.CONTACT_EMAIL_FROM, 'CONTACT_EMAIL_FROM', 'onlinerequest@insyncanalytics.com');
export const CONTACT_EMAIL_TO = readEnv(process.env.CONTACT_EMAIL_TO, 'CONTACT_EMAIL_TO', 'Support@insyncanalytics.com');

// Batch Settings
export const MAX_BATCH_SIZE = readEnvNumber(process.env.MAX_BATCH_SIZE, 'MAX_BATCH_SIZE', 10000);
export const MAX_PARALLEL_REQUESTS = readEnvNumber(process.env.MAX_PARALLEL_REQUESTS, 'MAX_PARALLEL_REQUESTS', 25);

// Cache Settings
export const CACHE_TTL_MINUTES = readEnvNumber(process.env.CACHE_TTL_MINUTES, 'CACHE_TTL_MINUTES', 30);
export const URL_CACHE_TTL_MINUTES = readEnvNumber(process.env.URL_CACHE_TTL_MINUTES, 'URL_CACHE_TTL_MINUTES', 10);

// Retry Settings
export const FAILURE_RETRY_SECONDS = readEnvNumber(process.env.FAILURE_RETRY_SECONDS, 'FAILURE_RETRY_SECONDS', 30);
export const MAX_RETRIES = readEnvNumber(process.env.MAX_RETRIES, 'MAX_RETRIES', 3);
export const IS_PROD = readEnvNumber(process.env.IS_PROD, 'IS_PROD', 0);

let rawUseNewHistoricalApi: string | undefined;
try {
  rawUseNewHistoricalApi = process.env.USE_NEW_HISTORICAL_API;
} catch {
  rawUseNewHistoricalApi = undefined;
}

export const USE_NEW_HISTORICAL_API = readEnvNumber(
  rawUseNewHistoricalApi,
  'USE_NEW_HISTORICAL_API',
  1
);
export const IS_DEVELOPING = !IS_PROD;

// -- Log shipping: two independent switches, one per destination ------------
//
// The two paths are deliberately separate so a cutover can run both, either or
// neither. Turning the new one on before the old one off gives a parallel run
// for reconciliation; turning the old one off is what stops the same entry
// being stored twice.
//
//   parallel run   API_LOGGING_ENABLED=1  TELEMETRY_ENABLED=1
//   cut over       API_LOGGING_ENABLED=1  TELEMETRY_ENABLED=0
//   roll back      API_LOGGING_ENABLED=0  TELEMETRY_ENABLED=1
//
// Both are compiled into the JS bundle at BUILD time, so changing either needs
// a rebuild and redeploy. They are not runtime toggles.

// OLD PATH: sanitized logs to the same-origin /telemetry endpoint
// (Caddy -> Vector -> Loki). Set to 0 to stop it and avoid storing the same
//
// PROD ONLY. /telemetry is served by Vector, which runs in the deployed
// observability stack and does not exist locally — so in a dev build every
// flush POSTs to https://localhost:3000/telemetry, gets a 404 from the webpack
// dev server, and adds nothing but console noise on a timer. Worse, a dev
// session that DID reach a real Vector would file local experiments into the
// same Loki stream as customer activity.
//
// Gating on IS_PROD rather than deleting the flag keeps the cutover matrix
// above intact: set this to 1 unconditionally to force a parallel run from a
// dev build when reconciling the two destinations.
//
// NOTE: this switches OFF only the /telemetry path. API_LOGGING_ENABLED still
// ships to /consensus_v2/addin-logs in dev, where LOG_API_TYPE routes it to the
// frontend_add_in_dev database — which is the intended destination for dev
// activity, so it is deliberately left on.
export const TELEMETRY_ENABLED = IS_PROD === 1
  ? readEnvNumber(process.env.TELEMETRY_ENABLED, 'TELEMETRY_ENABLED', 1)
  : 0;
export const TELEMETRY_ENDPOINT =  '/telemetry';

export const API_LOGGING_ENABLED = 1;
export const API_LOGGING_ENDPOINT = '/consensus_v2/addin-logs';

// Whether credential VALUES may be written into the log store.
//
// 0 (default) scrubs DATA_DELIVERY_V2_PASSWORD, BACKGROUND_FETCHER_PASSWORD,
// TEMP_TOKEN and CONTACT_EMAIL_BASIC_PASSWORD out of every message, data blob,
// error and URL before it is shipped - wherever they appear, not just in fields
// we happened to anticipate. All four are compiled into the bundle and any of
// them landing in a queryable log store means anyone who can read the logs can
// replay them.
//
// Set to 1 ONLY for local debugging of an auth problem, and never in a build
// that ships. Defaults to 0 so the safe behaviour is the one you get by
// forgetting about it.
// Typed as number, not narrowed to the literal 0, so the `=== 1` check in
// actionLog.ts compiles and the flag can actually be flipped.
export const LOG_SAVE_AUTH: number = 0;
export const APP_VERSION = readEnv(process.env.APP_VERSION, 'APP_VERSION', '0.0.0');

// affected. TESTING VALUE (30 seconds) — set to 30 * 60 * 1000 for release.
export const OTP_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

// Export as config object for convenience
export const config = {
  API_BASE_URL,
  DATA_DELIVERY_URL,
  ISTARI_BASE_URL,
  ISTARI_NEW_BASE_URL,
  ISTARI_NEW_PROXY_URL,
  ISTARI_PROXY_URL,
  LEGACY_PROXY_URL,
  RMS_BASE_URL,
  DATA_DELIVERY_PROXY_URL,

  HTTP_TIMEOUT_MINUTES,
  HTTP_TIMEOUT_SHORT_MINUTES,
  HTTP_TIMEOUT_LONG_MINUTES,
  USER_AGENT,
  CONTACT_EMAIL_FROM,
  CONTACT_EMAIL_TO,

  MAX_BATCH_SIZE,
  MAX_PARALLEL_REQUESTS,

  CACHE_TTL_MINUTES,
  URL_CACHE_TTL_MINUTES,

  FAILURE_RETRY_SECONDS,
  MAX_RETRIES,
  TEMP_TOKEN,
  IS_PROD,
  USE_NEW_HISTORICAL_API,
  IS_DEVELOPING,
  TELEMETRY_ENABLED,
  TELEMETRY_ENDPOINT,
  OTP_IDLE_TIMEOUT_MS,
  API_LOGGING_ENABLED,
  API_LOGGING_ENDPOINT,
  LOG_SAVE_AUTH,
  APP_VERSION,
  // Computed values
  HTTP_TIMEOUT_MS: HTTP_TIMEOUT_MINUTES * 60 * 1000,
  CACHE_TTL_MS: CACHE_TTL_MINUTES * 60 * 1000,
} as const;

export default config;
